import React, { useState } from 'react';
import { PickingTask } from '../../services/api';
import { PickingTasksList } from './PickingTasksList';
import { GuidedPickingInterface } from './GuidedPickingInterface';
import { QCCheckpoint } from './QCCheckpoint';
import { CubeIcon, ClipboardDocumentListIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

type ViewMode = 'list' | 'picking' | 'qc' | 'packing-slip';

interface PickingPackingPageProps {
  dealId?: number;
}

export const PickingPackingPage: React.FC<PickingPackingPageProps> = ({ dealId }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedTask, setSelectedTask] = useState<PickingTask | null>(null);

  const handleSelectTask = (task: PickingTask) => {
    setSelectedTask(task);
    // Determine which view to show based on task status
    if (task.status === 'TO_PICK' || task.status === 'IN_PROGRESS') {
      setViewMode('picking');
    } else if (['PICKED', 'QC_PENDING', 'QC_PASSED', 'QC_FAILED', 'PACKED'].includes(task.status)) {
      setViewMode('qc');
    } else {
      setViewMode('list');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedTask(null);
  };

  const handlePickingComplete = () => {
    if (selectedTask) {
      setViewMode('qc');
    }
  };

  const handleQCComplete = () => {
    setViewMode('list');
    setSelectedTask(null);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <CubeIcon className="w-8 h-8 text-blue-600" />
          Picking & Packing
        </h1>
        <p className="text-gray-600 mt-1">
          Manage order fulfillment with guided picking, QC verification, and packing documentation
        </p>
      </div>

      {/* Workflow Steps Indicator */}
      {viewMode !== 'list' && selectedTask && (
        <div className="mb-6 bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <div className={`flex flex-col items-center ${['picking', 'qc', 'packing-slip'].includes(viewMode) ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedTask.status === 'TO_PICK' || selectedTask.status === 'IN_PROGRESS' ? 'bg-blue-600 text-white' : selectedTask.pickedAt ? 'bg-green-100 text-green-600' : 'bg-gray-100'}`}>
                <ClipboardDocumentListIcon className="w-5 h-5" />
              </div>
              <span className="text-xs mt-1 font-medium">Picking</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-2">
              <div className={`h-full ${selectedTask.pickedAt ? 'bg-green-500' : 'bg-gray-200'}`} />
            </div>
            <div className={`flex flex-col items-center ${['qc', 'packing-slip'].includes(viewMode) ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedTask.status === 'QC_PENDING' ? 'bg-yellow-500 text-white' : selectedTask.qcPassed ? 'bg-green-100 text-green-600' : selectedTask.status === 'QC_FAILED' ? 'bg-red-100 text-red-600' : 'bg-gray-100'}`}>
                <CheckBadgeIcon className="w-5 h-5" />
              </div>
              <span className="text-xs mt-1 font-medium">QC Check</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-2">
              <div className={`h-full ${selectedTask.qcPassed ? 'bg-green-500' : 'bg-gray-200'}`} />
            </div>
            <div className={`flex flex-col items-center ${viewMode === 'packing-slip' ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedTask.status === 'PACKED' ? 'bg-emerald-500 text-white' : selectedTask.status === 'QC_PASSED' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100'}`}>
                <CubeIcon className="w-5 h-5" />
              </div>
              <span className="text-xs mt-1 font-medium">Packing</span>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {viewMode === 'list' && (
        <PickingTasksList onSelectTask={handleSelectTask} dealId={dealId} />
      )}

      {viewMode === 'picking' && selectedTask && (
        <GuidedPickingInterface
          taskId={selectedTask.id}
          onBack={handleBack}
          onComplete={handlePickingComplete}
        />
      )}

      {viewMode === 'qc' && selectedTask && (
        <QCCheckpoint
          taskId={selectedTask.id}
          onBack={handleBack}
          onComplete={handleQCComplete}
        />
      )}
    </div>
  );
};

export default PickingPackingPage;

